import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ExternalLink, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const YelpProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const keyStats = [
    { value: "65%", label: "High business closure rates in first year" },
    { value: "42%", label: "Insufficient capital access" },
    { value: "78%", label: "Intense market competition" },
  ];

  const findings = [
    {
      title: "Talent Acquisition Struggles",
      problem: "Sourcing qualified candidates remains a persistent challenge for emerging businesses, compounded by high employee turnover rates and difficulties in candidate quality assessment during hiring processes.",
      opportunity: "Mentorship around recruitment strategies, team building, and retention best practices.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A9b81818d-131f-4bca-9172-793b6548ce80%3Aimage_73.png?table=block&id=2ef22ee1-b108-805c-88fb-d9f37a59cb3a&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2",
    },
    {
      title: "Knowledge Gaps & Resource Reliability",
      problem: "The absence of structured guidance forces new business owners to seek information from unreliable or inadequate sources, leading to misinformed decisions and wasted resources.",
      opportunity: "Direct access to experienced entrepreneurs who can provide vetted, actionable advice based on real-world experience.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A31999aba-54bd-4cbe-af06-dfff4459f40c%3Aimage_74.png?table=block&id=2ef22ee1-b108-80ea-af3e-f6a72995628e&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2",
    },
    {
      title: "Marketing & Promotional Constraints",
      problem: "While business owners recognize the importance of advertising for growth, limited expertise and severe time constraints significantly interfere with effective promotional execution.",
      opportunity: "Guidance on cost-effective marketing strategies and time-efficient promotional tactics.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Ae7db9cf9-d322-4516-a22b-1d9a2a04afd0%3Aimage_75.png?table=block&id=2ef22ee1-b108-8027-8ff9-e193c2251c76&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2",
    },
    {
      title: "Financial Navigation Challenges",
      problem: "New business owners require financial assistance during startup phases but experience confusion regarding funding sources, application processes, and financial planning strategies.",
      opportunity: "Mentorship on navigating funding options, financial modeling, and sustainable financial management.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Af4c0fcae-8f87-49a5-8519-399e9df2757b%3Aimage_76.png?table=block&id=2ef22ee1-b108-80dc-9dab-e908896e4acf&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2",
    },
  ];

  const features = [
    {
      number: "01",
      title: "Personalized Dashboard",
      purpose: "Centralized overview of mentorship activities and engagement",
      description: "At-a-glance visibility into upcoming sessions, ongoing mentorship relationships, and recent activity updates, enabling efficient time management and relationship tracking.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Acb97d8a1-f4ca-450a-823d-04947e7a9344%3Aimage_88.png?table=block&id=2f022ee1-b108-80b5-ba58-ec5ce45a7e0f&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "02",
      title: "Intelligent Mentor Discovery",
      purpose: "Strategic matching between entrepreneurs and relevant mentors",
      description: "Advanced filtering system by industry niche, expertise areas, availability, and mentor specializations, facilitating precise, relevant mentor-mentee matching aligned with specific business needs.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Ab485e028-af57-4967-922c-b954bf7cdc9a%3Aimage_90.png?table=block&id=2f522ee1-b108-802c-9e4a-d56c52a93fc6&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "03",
      title: "Comprehensive Mentor Profiles",
      purpose: "Transparent evaluation of mentor credentials and expertise",
      description: "Detailed profiles showcasing professional background, areas of expertise, mentorship philosophy, testimonials from previous mentees, and direct communication capabilities.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Adbbb01da-6952-4226-ba6c-9e40baf7633c%3Aimage_89.png?table=block&id=2f522ee1-b108-80e6-9797-f5b50af0c6c2&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=770&userId=&cache=v2",
    },
    {
      number: "04",
      title: "Flexible Scheduling Interface",
      purpose: "Streamlined appointment coordination",
      description: "Real-time availability display integrated with calendar systems, allowing seamless session booking aligned with both mentor and mentee schedules.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A2cfbd776-fb8b-4dd3-bbe3-a800e40fdffe%3Aimage_92.png?table=block&id=2f522ee1-b108-8086-942a-e47f4d91f605&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=770&userId=&cache=v2",
    },
    {
      number: "05",
      title: "Session Management & Knowledge Repository",
      purpose: "Comprehensive tracking and documentation",
      description: "Organized view of sessions with integrated note-taking capabilities, AI-powered session summaries, and a searchable knowledge archive enabling entrepreneurs to revisit key insights.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A33bfef1f-5ebb-49d7-a716-0d8c10a4ed59%3Aimage_91.png?table=block&id=2f522ee1-b108-8068-9cbb-ddc5dbf68336&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=770&userId=&cache=v2",
    },
  ];

  const valueProps = [
    { title: "Accessibility", desc: "Democratizing access to experienced business mentorship" },
    { title: "Credibility", desc: "Verified mentor profiles with testimonials and track records" },
    { title: "Continuity", desc: "Ongoing relationships through various business growth stages" },
    { title: "Customization", desc: "Industry-specific expertise matching for relevant advice" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-blue-500/10 text-blue-400 border-blue-500/20">
              HRTech
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              Yelp Mentorship Platform
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              A Scalable Mentorship System for Entrepreneurial Growth
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>UX Researcher & Product Designer</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>8 weeks (Capstone Project)</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Figma, Miro, User Interviews</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/vnls4t6f_Gemini_Generated_Image_xn60aoxn60aoxn60.png"
              alt="Yelp Mentorship Platform"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Design Challenge */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              The Design Challenge
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl">
              How might we connect local businesses and aspiring entrepreneurs to meaningful resources that enable sustainable growth and long-term success?
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12 rounded-3xl overflow-hidden"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A481f74dc-f003-40d7-90b3-4fd130b85149%3AUntitled_2024-02-01_19-26-52_1.png?table=block&id=2ee22ee1-b108-805d-a54d-f0ba8bdacb02&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="Design Challenge Visual"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Problem Space */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Problem Space & Context
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-4">
              The Reality for Small Businesses
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mb-12">
              Small business owners face overwhelming challenges that threaten their survival, particularly within the critical first seven years of operation. The lack of accessible, trustworthy guidance creates a sustainability crisis that impacts economic growth and community vitality.
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            {keyStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
                <p className="text-white/60 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Findings */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Research Insights
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Critical Findings & Opportunities
            </h2>
          </motion.div>

          <div>
            {findings.map((finding, index) => (
              <motion.div
                key={finding.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid md:grid-cols-2 gap-16 items-center${index > 0 ? ' mt-20' : ''}`}
              >
                <div className={index % 2 === 1 ? "md:order-2" : ""}>
                  <div className="rounded-2xl overflow-hidden bg-[#121212] border border-white/5">
                    <img src={finding.image} alt={finding.title} className="w-full" />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "md:order-1" : ""}>
                  <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">
                    {finding.title}
                  </h3>
                  <div className="mb-6">
                    <span className="text-sm font-semibold text-red-400 block mb-2">Problem:</span>
                    <p className="text-white/60 leading-relaxed">{finding.problem}</p>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-green-400 block mb-2">Opportunity:</span>
                    <p className="text-white/60 leading-relaxed">{finding.opportunity}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              The Solution
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              A Mentorship-Driven Platform
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              A dedicated platform that facilitates meaningful connections between small business owners, aspiring entrepreneurs, and experienced mentors who provide strategic guidance, actionable advice, and ongoing support.
            </p>
          </motion.div>

          {/* Value Props */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueProps.map((prop, index) => (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors"
              >
                <h3 className="text-lg font-bold text-white mb-2">{prop.title}</h3>
                <p className="text-white/50 text-sm">{prop.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Flow */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Design Process
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              User Flow Development
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-[#121212] border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A82dea5e9-a125-4883-8933-d3038613ef7a%3Aimage_80.png?table=block&id=2ef22ee1-b108-8053-9d4d-d7411683ef4c&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="User Flow"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Wireframes */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Wireframes
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Low-Med Fidelity Wireframes
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-[#121212] border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3Ac9c879b0-23ec-4b1a-8c4f-91db25d934a0%3Aimage.png?table=block&id=2f022ee1-b108-80bf-9cb8-ee3316f46c2e&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1540&userId=&cache=v2"
              alt="Wireframes"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Key Features
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Core User Flows
            </h2>
          </motion.div>

          <div>
            {features.map((feature, index) => (
              <motion.div
                key={feature.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid md:grid-cols-5 gap-10 items-center${index > 0 ? ' mt-16' : ''}`}
              >
                <div className={`md:col-span-3 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="rounded-3xl overflow-hidden border border-white/5">
                    <img src={feature.image} alt={feature.title} className="w-full rounded-3xl" />
                  </div>
                </div>
                <div className={`md:col-span-2 ${index % 2 === 1 ? "md:order-1" : ""}`}>
                  <span className="text-5xl font-bold text-white/10 font-['Syne']">{feature.number}</span>
                  <h3 className="text-2xl font-bold font-['Syne'] text-white mt-2 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-blue-400 text-sm font-medium mb-3">{feature.purpose}</p>
                  <p className="text-white/60">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reflections */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Reflections
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Key Learnings
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Human-Centered Research</h3>
              <p className="text-white/60 text-sm">
                Direct conversations with business owners revealed nuanced pain points that secondary research alone could not uncover, emphasizing the irreplaceable value of primary user research.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Resource Constraints Reality</h3>
              <p className="text-white/60 text-sm">
                Understanding that time and money are finite resources for small businesses shaped design decisions toward efficiency and value delivery rather than feature abundance.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/5"
            >
              <h3 className="text-lg font-bold text-white mb-3">Trust as Foundation</h3>
              <p className="text-white/60 text-sm">
                For a mentorship platform to succeed, establishing credibility and trust through transparency, social proof, and verified expertise is essential to user adoption.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              The Yelp Mentorship Platform represents a strategic intervention addressing systemic challenges faced by small business owners. By transforming experienced entrepreneurial knowledge into an accessible, trustworthy resource, the platform has the potential to reduce business failure rates, strengthen local economies, and democratize success in entrepreneurship.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/cardinal")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Next: Cardinal
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YelpProject;
